import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styles from '../../static/styles/PuzzleRotate.module.css';
import { submitGameExit } from '../../api/games';
import MessagePopup from '../../components/MessagePopup';

const PuzzleRotate = () => {
  const { t } = useTranslation();
  const [gridSize, setGridSize] = useState(3);
  const [maxMoves, setMaxMoves] = useState(18);
  const [moves, setMoves] = useState(0);
  const [time, setTime] = useState(0);
  const [started, setStarted] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [tiles, setTiles] = useState([]);
  const [successMessage, setSuccessMessage] = useState('');
  const [failureMessage, setFailureMessage] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [showFailure, setShowFailure] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupMessage, setPopupMessage] = useState('');
  const [randomImage, setRandomImage] = useState(null);

  const timerRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const puzzleManiaState = location.state;

  const totalTiles = gridSize * gridSize;
  const tileSize = 300 / gridSize;

  useEffect(() => {
    document.title = `${t('puzzle_mania')} | Jazz Esl`;
    
    // Override layout background for cyberpunk animations
    const mainContent = document.querySelector('[class*="mainContent"]');
    const mainContentInner = document.querySelector('[class*="mainContentInner"]');
    
    if (mainContent) {
      mainContent.style.background = 'transparent';
    }
    if (mainContentInner) {
      mainContentInner.style.background = 'transparent';
    }
    
    return () => {
      // Restore original background on cleanup
      if (mainContent) {
        mainContent.style.background = '#1a1a1a';
      }
      if (mainContentInner) {
        mainContent.style.background = '#121212';
      }
    };
  }, [t]);

  useEffect(() => {
    if (!puzzleManiaState) {
      navigate('/daily-gaming-tasks');
      return;
    }
    
    if (puzzleManiaState.game_level) {
      const level = parseInt(puzzleManiaState.game_level);
      if (level === 1) {
        setGridSize(3);
      } else if (level === 2) {
        setGridSize(4);
      } else if (level === 3) {
        setGridSize(5);
      }
    }
  }, [puzzleManiaState, navigate]);

  const spawnParticles = useCallback(() => {
    const bg = document.getElementById('animated-bg');
    if (!bg) return;
    
    // Calculate content width (account for sidebar on desktop)
    const isMobile = window.innerWidth <= 768;
    const contentWidth = isMobile ? window.innerWidth : window.innerWidth - 270;
    
    // Reduce particle count on smaller devices for better performance
    const particleCount = window.innerWidth <= 480 ? 8 : window.innerWidth <= 768 ? 12 : 20;
    
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return;
    
    for (let i = 0; i < particleCount; i++) {
      const p = document.createElement('div');
      p.className = styles.particle;
      const size = Math.random() * 6 + 4;
      p.style.width = p.style.height = size + 'px';
      p.style.left = Math.random() * contentWidth + 'px';
      p.style.animationDuration = (3 + Math.random() * 5) + 's';
      bg.appendChild(p);
      setTimeout(() => p.remove(), 9000);
    }
  }, []);

  useEffect(() => {
    const interval = window.innerWidth <= 480 ? 4000 : window.innerWidth <= 768 ? 3000 : 2000;
    const particleInterval = setInterval(spawnParticles, interval);
    return () => clearInterval(particleInterval);
  }, [spawnParticles]);

  const calculateMinimumMoves = useCallback((tiles) => {
    // Count how many rotations each tile needs
    let totalRotationsNeeded = 0;
    tiles.forEach(tile => {
      if (tile.rotation > 0) {
        const rotationsNeeded = Math.min(tile.rotation, 4 - tile.rotation);
        totalRotationsNeeded += rotationsNeeded;
      }
    });
    return totalRotationsNeeded;
  }, []);

  const initializeTiles = useCallback(() => {
    const newTiles = [];
    for (let i = 0; i < totalTiles; i++) {
      newTiles.push({
        id: i,
        rotation: 0, // Start with correct rotation
        backgroundPosition: `-${(i % gridSize) * tileSize}px -${Math.floor(i / gridSize) * tileSize}px`
      });
    }
    
    const shuffledTiles = [...newTiles];
    
    const targetRotatedTiles = Math.ceil(totalTiles * (gridSize === 3 ? 0.7 : gridSize === 4 ? 0.6 : 0.5));
    
    const tilesToRotate = [];
    while (tilesToRotate.length < targetRotatedTiles) {
      const randomIndex = Math.floor(Math.random() * totalTiles);
      if (!tilesToRotate.includes(randomIndex)) {
        tilesToRotate.push(randomIndex);
      }
    }
    
    tilesToRotate.forEach(tileIndex => {
      const rotations = Math.floor(Math.random() * 3) + 1;
      shuffledTiles[tileIndex].rotation = rotations;
    });
    
    const minimumMoves = calculateMinimumMoves(shuffledTiles);
    const bufferMultiplier = gridSize === 3 ? 2.0 : gridSize === 4 ? 1.8 : 1.6;
    const calculatedMaxMoves = Math.ceil(minimumMoves * bufferMultiplier);
    
    // Add extra breathing room on top of calculated moves
    const extraBuffer = gridSize === 3 ? 5 : gridSize === 4 ? 6 : 7;
    const finalMaxMoves = calculatedMaxMoves + extraBuffer;
    
    //console.log(`Puzzle generated - Grid: ${gridSize}x${gridSize}, Rotated tiles: ${tilesToRotate.length}/${totalTiles}, Min moves: ${minimumMoves}, Allowed: ${finalMaxMoves}`);
    
    setMaxMoves(finalMaxMoves);
    setTiles(shuffledTiles);
  }, [totalTiles, gridSize, tileSize, calculateMinimumMoves]);

  const resetGame = useCallback(() => {
    setMoves(0);
    setTime(0);
    setStarted(false);
    setGameOver(false);
    setSuccessMessage('');
    setFailureMessage('');
    setShowSuccess(false);
    setShowFailure(false);
    
    const randomIndex = Math.floor(Math.random() * 30) + 1;
    const imageImport = require(`../../static/img/puzzle-rotate/img_${randomIndex}.avif`);
    setRandomImage(imageImport);
    
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    
    initializeTiles();
  }, [initializeTiles]);

  useEffect(() => {
    resetGame();
  }, [resetGame]);

  const startTimer = useCallback(() => {
    if (!started && !gameOver) {
      setStarted(true);
      timerRef.current = setInterval(() => {
        setTime(prevTime => prevTime + 1);
      }, 1000);
    }
  }, [started, gameOver]);

  const checkSuccess = useCallback((updatedTiles, currentMoves, currentTime) => {
    const allCorrect = updatedTiles.every(tile => tile.rotation === 0);
    if (allCorrect) {
      setGameOver(true);
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      setSuccessMessage(t('success_join'));
      setShowSuccess(true);

      // Submit game success if coming from challenges
      if (puzzleManiaState) {
        submitGameExit({
          game_unique_id: puzzleManiaState.game_unique_id,
          game_name: puzzleManiaState.game_name,
          game_level: puzzleManiaState.game_level,
          level_credits: puzzleManiaState.level_credits,
          is_passed: "1"
        }).then(() => {
          setPopupMessage(t('pass_lvl', { lvl_num: puzzleManiaState.game_level }));
          setPopupOpen(true);
        }).catch(() => {
          setPopupMessage(t('success_join'));
          setPopupOpen(true);
        });
      }
    }
  }, [puzzleManiaState, t]);

  const rotateTile = (tileId) => {
    if (gameOver) return;

    startTimer();

    const newMoves = moves + 1;
    setMoves(newMoves);

    // Update tiles and check success in the same operation
    setTiles(prevTiles => {
      const updatedTiles = prevTiles.map(tile => 
        tile.id === tileId 
          ? { ...tile, rotation: (tile.rotation + 1) % 4 }
          : tile
      );

      // Check for success immediately after updating tiles
      // Use current time state for accurate reporting
      setTimeout(() => {
        checkSuccess(updatedTiles, newMoves, time);
      }, 0);

      return updatedTiles;
    });

    if (newMoves > maxMoves) {
      setGameOver(true);
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      setFailureMessage(t('err_long'));
      setShowFailure(true);

      // Submit game failure if coming from challenges
      if (puzzleManiaState) {
        submitGameExit({
          game_unique_id: puzzleManiaState.game_unique_id,
          game_name: puzzleManiaState.game_name,
          game_level: puzzleManiaState.game_level,
          level_credits: puzzleManiaState.level_credits,
          is_passed: "0"
        }).then(() => {
          setPopupMessage(t('beat_lvl', { lvl_num: puzzleManiaState.game_level }));
          setPopupOpen(true);
        }).catch(() => {
          setPopupMessage(t('err_long'));
          setPopupOpen(true);
        });
      }
      return;
    }
  };

  const handleDifficultyChange = (size) => {
    if (gameOver) return;
    // If coming from challenges, don't allow difficulty change
    if (puzzleManiaState) return;
    setGridSize(size);
    // Reset will be triggered by useEffect dependency
  };

  const handlePopupClose = () => {
    setPopupOpen(false);
    if (puzzleManiaState) {
      navigate('/daily-gaming-tasks');
    }
  };

  useEffect(() => {
    resetGame();
  }, [gridSize, maxMoves, resetGame]);

  const allDifficultyButtons = [
    { size: 3, label: t('easy') },
    { size: 4, label: t('medium') },
    { size: 5, label: t('hard') }
  ];

  // If coming from challenges, only show the active difficulty button
  const difficultyButtons = puzzleManiaState 
    ? allDifficultyButtons.filter(button => button.size === gridSize)
    : allDifficultyButtons;

  return (
    <div className={styles.body}>
      <div id="animated-bg" className={styles.animatedBg}>
        <div className={styles.grid}></div>
      </div>
      
      <div className={styles.neonGlow}></div>
      <div className={styles.scanlines}></div>

      <h1 className={styles.title}>{t('puzzle_mania').toUpperCase()}</h1>

      <div className={styles.controls}>
        {difficultyButtons.map(({ size, label }) => (
          <button
            key={size}
            className={`${styles.difficultyBtn} ${gridSize === size ? styles.active : ''}`}
            onClick={() => handleDifficultyChange(size)}
            disabled={gameOver}
          >
            {label}
          </button>
        ))}
      </div>

      <div className={styles.mainWrapper}>
        <div 
          className={styles.gameContainer}
          style={{
            gridTemplateColumns: `repeat(${gridSize}, ${tileSize}px)`,
            gridTemplateRows: `repeat(${gridSize}, ${tileSize}px)`
          }}
        >
          {tiles.map(tile => (
            <div
              key={tile.id}
              className={styles.tile}
              style={{
                width: `${tileSize}px`,
                height: `${tileSize}px`,
                transform: `rotate(${tile.rotation * 90}deg)`,
                backgroundImage: `url(${randomImage})`,
                backgroundPosition: tile.backgroundPosition,
                backgroundSize: '300px 300px'
              }}
              onClick={() => rotateTile(tile.id)}
            />
          ))}
        </div>
        
        <div className={styles.referenceImage}>
          <img 
            src={randomImage} 
            alt="Reference" 
            className={styles.referenceImg}
          />
        </div>
      </div>

      <div className={styles.info}>
        <span className={styles.infoBadge}>{t('loading')}: <span>{moves}</span></span>
        <span className={styles.infoBadge}>{t('loading')}: <span>{time}</span> sec</span>
        <span className={styles.infoBadge}>{t('loading')}: <span>{maxMoves}</span></span>
      </div>

      {showSuccess && (
        <div className={`${styles.success} ${showSuccess ? styles.show : ''}`}>
          {successMessage}
        </div>
      )}

      {showFailure && (
        <div className={`${styles.failure} ${showFailure ? styles.show : ''}`}>
          {failureMessage}
        </div>
      )}

      <button
        className={styles.resetBtn}
        onClick={resetGame}
      >
        {t('loading')}
      </button>

      <MessagePopup
        isOpen={popupOpen}
        message={popupMessage}
        onClose={handlePopupClose}
      />
    </div>
  );
};


export default PuzzleRotate;